// src/pages/auth/AuthContainer.jsx
import React from 'react';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #2E2A35;
  background-image: radial-gradient(rgba(255, 255, 255, 0.08) 1.5px, transparent 1.5px);
  background-size: 32px 32px;
  background-position: 0 0;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 1rem;
  }
`;

const LeftPanel = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    display: none;
  }
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 500px;
    padding: 0;
  }

  @media (max-width: 480px) {
    max-width: 100%;
    padding: 0;
  }
`;

const FormCard = styled.div`
  background: rgba(252, 250, 247, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 2rem;
  width: 100%;
  max-width: 450px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(221, 212, 204, 0.3);
  position: relative;
  margin: auto;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 2rem 1.75rem;
    border-radius: 20px;
    margin: 0;
  }

  @media (max-width: 480px) {
    padding: 2rem 1.5rem;
    border-radius: 16px;
  }
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 1.25rem;

  img {
    width: 120px;
    height: auto;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;

    img {
      width: 100px;
    }
  }
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 800;
  text-align: center;
  margin: 0 0 0.4rem 0;
  background: #201C18;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  color: #6A625B;
  font-size: 0.9rem;
  margin: 0 0 1.5rem 0;
  line-height: 1.4;

  @media (max-width: 480px) {
    font-size: 0.85rem;
    margin: 0 0 1.25rem 0;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  margin: 1.5rem 0;
  color: #94a3b8;
  font-size: 0.875rem;
  font-weight: 500;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #e2e8f0;
  }

  &::before {
    margin-right: 1rem;
  }

  &::after {
    margin-left: 1rem;
  }
`;

const FooterLinks = styled.div`
  text-align: center;
  margin-top: 1.25rem;
  
  p {
    color: #6A625B;
    font-size: 0.85rem;
    margin: 0.4rem 0;
  }

  a {
    color: #C86A4A;
    text-decoration: none;
    font-weight: 600;
    margin-left: 0.25rem;
    transition: color 0.2s ease;

    &:hover {
      color: #B65B3C;
      text-decoration: underline;
    }
  }

  @media (max-width: 480px) {
    margin-top: 1rem;

    p {
      font-size: 0.8rem;
    }
  }
`;

const AuthContainer = ({
  leftContent,
  logo,
  title,
  subtitle,
  children,
  footerLinks,
  showDivider = false
}) => {
  return (
    <Container>
      <LeftPanel>
        {leftContent}
      </LeftPanel>
      <RightPanel>
        <FormCard>
          {logo && <Logo>{logo}</Logo>}
          {title && <Title>{title}</Title>}
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
          {showDivider && <Divider>OR</Divider>}
          {children}
          {footerLinks && <FooterLinks>{footerLinks}</FooterLinks>}
        </FormCard>
      </RightPanel>
    </Container>
  );
};

export default AuthContainer;
export { Divider, FooterLinks };
